import { TextDirection } from '../../../localization/textDirection';
import { DirectoryRater, AddedDirectoryRater, Rater } from './models';
import { Requirement } from '../../models';
import { isValidDate } from '../../../language/time';
import { Map } from '../../../language/models';
import * as R from 'ramda';


/**
 * Returns a name string in the form `firstName lastName`, formatted left-to-right
 *
 * @param named - a `Rater` or other object with `firstName` and `lastName` properties
 * @returns
 * * undefined if `named` does not exist or has no first or last name
 * * the `lastName` property of `named` if `firstName` is missing
 * * the `firstName` property of `named` if `lastName` is missing
 * * otherwise a name string formatted `firstName lastName`
 */
export const firstLast = (
  named: { firstName: string | undefined; lastName: string | undefined; } | undefined,
): string | undefined => {
  if (!named || (!named.firstName && !named.lastName)) {
    return undefined;
  }

  if (!named.firstName) {
    return named.lastName;
  }

  if (!named.lastName) {
    return named.firstName;
  }

  return `${named.firstName} ${named.lastName}`;
};


/**
 * Returns a name string in the form `firstName lastName`, formatted right-to-left
 *
 * @param named - a `Rater` or other object with `firstName` and `lastName` properties
 * @returns
 * * undefined if `named` does not exist or has no first or last name
 * * the `lastName` property of `named` if `firstName` is missing
 * * the `firstName` property of `named` if `lastName` is missing
 * * otherwise a name string formatted `lastName firstName`
 */
export const firstLastReverse = (
  named: { firstName: string | undefined; lastName: string | undefined; } | undefined,
): string | undefined => {
  if (!named || (!named.firstName && !named.lastName)) {
    return undefined;
  }

  if (!named.firstName) {
    return named.lastName;
  }

  if (!named.lastName) {
    return named.firstName;
  }

  return `${named.lastName} ${named.firstName}`;
};

/**
 * Returns a name string in the form `firstName lastName`, formatted to match the provided text direction
 *
 * @param dir - the text direction which the name string should be formatted to match
 * @param named - a `Rater` or other object with `firstName` and `lastName` properties
 * @returns
 * * undefined if `named` does not exist or has no first or last name
 * * the `lastName` property of `named` if `firstName` is missing
 * * the `firstName` property of `named` if `lastName` is missing
 * * otherwise a full name string of the form `firstName lastName`, formatted for the provided text direction
 */
export const firstLastLocal = (
  dir: TextDirection,
  named: { firstName: string | undefined; lastName: string | undefined; } | undefined,
): string | undefined => dir === TextDirection.LTR ? firstLast(named) : firstLastReverse(named);


/**
 * Returns a name string in the form `lastName, firstName`, formatted left-to-right
 *
 * @param named - a `Rater` or other object with `firstName` and `lastName` properties
 * @returns
 * * undefined if `named` does not exist or has no first or last name
 * * the `lastName` property of `named` if `firstName` is missing
 * * the `firstName` property of `named` if `lastName` is missing
 * * otherwise a name string formatted `lastName, firstName`
 */
export const lastCommaFirst = (
  named: { firstName: string | undefined; lastName: string | undefined; } | undefined,
): string | undefined => {
  if (!named || (!named.firstName && !named.lastName)) {
    return undefined;
  }

  if (!named.firstName) {
    return named.lastName;
  }

  if (!named.lastName) {
    return named.firstName;
  }

  return `${named.lastName}, ${named.firstName}`;
};


/**
 * Returns a name string in the form `lastName, firstName`, formatted right-to-left
 *
 * @param named - a `Rater` or other object with `firstName` and `lastName` properties
 * @returns
 * * undefined if `named` does not exist or has no first or last name
 * * the `lastName` property of `named` if `firstName` is missing
 * * the `firstName` property of `named` if `lastName` is missing
 * * otherwise a name string formatted `firstName ,lastName`
 */
export const lastCommaFirstReverse = (
  named: { firstName: string | undefined; lastName: string | undefined; } | undefined,
): string | undefined => {
  if (!named || (!named.firstName && !named.lastName)) {
    return undefined;
  }

  if (!named.firstName) {
    return named.lastName;
  }

  if (!named.lastName) {
    return named.firstName;
  }

  return `${named.firstName} ,${named.lastName}`;
};


/**
 * Returns a name string in the form `lastName, firstName`, formatted to match the provided text direction
 *
 * @param dir - the text direction which the name string should be formatted to match
 * @param named - a `Rater` or other object with `firstName` and `lastName` properties
 * @returns
 * * undefined if `named` does not exist or has no first or last name
 * * the `lastName` property of `named` if `firstName` is missing
 * * the `firstName` property of `named` if `lastName` is missing
 * * otherwise a full name string of the form `lastName, firstName`, formatted for the provided text direction
 */
export const lastCommaFirstLocal = (
  dir: TextDirection,
  named: { firstName: string | undefined; lastName: string | undefined; } | undefined,
): string | undefined => dir === TextDirection.LTR ? lastCommaFirst(named) : lastCommaFirstReverse(named);


/**
 * Returns a name string in the form `lastName, firstName`, formatted to match the provided text direction, or the
 * user's email address in the event that the user's name string is undefined or empty
 *
 * @param dir - the text direction which the name string should be formatted to match
 * @param rater - the rater whose name or email address we wish to retrieve
 * @returns
 * * the `lastName` property of `rater` if `firstName` is missing
 * * the `firstName` property of `rater` if `lastName` is missing
 * * a full name string of the form `lastName, firstName`, formatted for the provided text direction, if both
 *   `firstName` and `lastName` properties are defined on `rater`
 * * otherwise the `email` property of `rater`
 */
export const nameOrEmail = (
  dir: TextDirection,
  rater: Rater | DirectoryRater | AddedDirectoryRater,
): string => lastCommaFirstLocal(dir, { firstName: rater.firstName, lastName: rater.lastName }) || rater.email;


/**
 * Returns all requirements in the provided array which have a valid required minimum. Used for rendering the list of
 * project requirements for the user when maximum rater counts are not mandatory, since a minimum of 0 entails that any
 * number of raters of the corresponding level is valid.
 *
 * @param requirements - the list of requirements to be filtered
 */
export const withValidMinimum = (
  requirements: Requirement[] | undefined,
) => requirements?.filter(r => r.minimumRequired > 0);


/**
 * Returns all requirements in the provided array which have a valid required minimum. Used for rendering the list of
 * project requirements for the user when maximum rater counts are mandatory (a non-zero minimum with a maximum of zero
 * signifies that any number of raters equal to or greater than the minimum required for that level is valid).
 *
 * @param requirements - the list of requirements to be filtered
 */
export const withValidMinOrMax = (
  requirements: Requirement[] | undefined,
) => requirements?.filter(r => r.minimumRequired > 0 || r.maximumRequired > 0);


/**
 * Returns the provided rater with the invite date updated to the provided date.
 *
 * @param rater - the rater to be updated
 * @param invitedDate - the invite date to set for the rater
 */
export const updateInvitedDateFor = (
  rater: Rater,
  invitedDate: Date | string,
): Rater => R.assoc('invitedDate', invitedDate, rater);


/**
 * Returns whether or not the given rater has an invite date and is thus invited.
 */
export const raterIsInvited = (rater: { invitedDate: string | Date }): boolean => isValidDate(rater.invitedDate);


/**
 * Returns a map of the provided, keyed as sub-arrays by their respective (stringified) level Ids.
 *
 * @param raters - the raters to be mapped by level Id
 */
export const ratersByLevel = (
  raters: Rater[],
): Map<Rater[]> => R.groupBy(rater => `${rater.levelId}`, raters);


/**
 * Returns the provided requirement, with the invited count updated to reflect the number of raters provided whose level
 * Id matches the rater level corresponding to that requirement
 *
 * @param requirement - the requirement whose invited count we wish to update
 * @param invitedRaters - a map of all newly invited raters (i.e., whose invite status is not currently reflected in the
 * requirement's invited count), mapped by level Id, that we must check in order to adjust the invited count
 */
export const updateInvitedCountFor = (
  requirement: Requirement,
  invitedRaters: Map<Rater[]>,
): Requirement => R.assoc(
  'invitedCount',
  requirement.invitedCount + (invitedRaters[`${requirement.surveyLevelId}`]?.length ?? 0),
  requirement,
);
